import Service from './Service';
import NavigationService from './NavigationService';
import EmploymentInfoService from '-/services/EmploymentInfoService';

const ADDITIONALINCOME_SETDATA = 'ADDITIONALINCOME_SETDATA';
const ADDITIONALINCOME_ADD = 'ADDITIONALINCOME_ADD';
const ADDITIONALINCOME_DELETE = 'ADDITIONALINCOME_DELETE';
const ADDITIONALINCOME_DELETE_ALL = 'ADDITIONALINCOME_DELETE_ALL';
const ADDITIONALINCOME_TYPE_CHANGE = 'ADDITIONALINCOME_TYPE_CHANGE';
const ADDITIONALINCOME_AMOUNT_CHANGE = 'ADDITIONALINCOME_AMOUNT_CHANGE';
const ADDITIONALINCOME_DESCRIPTION_CHANGE = 'ADDITIONALINCOME_DESCRIPTION_CHANGE';
const ADDITIONALINCOME_MONTH_CHANGE = 'ADDITIONALINCOME_MONTH_CHANGE';
const ADDITIONALINCOME_YEAR_CHANGE = 'ADDITIONALINCOME_YEAR_CHANGE';
const ADDITIONALINCOME_SET = 'ADDITIONALINCOME_SET';
const CLEAR_COAPP = 'CLEAR_COAPP';

const reducerName = 'additionalIncome';

const setState = (state, key, value) => {
    const keyName = getKeyName();

    return {
        ...state,
        [keyName]: {
            ...state[keyName],
            [key]: value
        }
    };
};

const getKeyName = () => {
    return `additionalIncome${NavigationService.isCoApplicant() ? '_CoApp' : ''}`;
};

const addMoreIncome = state => {
    const key = getKeyName();
    return {
        ...state,
        [key]: {
            ...state[key],
            entries: [
                ...state[key].entries || [],
                {
                    type: '',
                    amount: ''
                }
            ]
        }
    };
};

const propertyChange = (state, index, value, property) => {
    const key = getKeyName();
    return {
        ...state,
        [key]: {
            ...state[key],
            entries: [
                ...state[key].entries.slice(0, index),
                {
                    ...state[key].entries[index],
                    [property]: value
                },
                ...state[key].entries.slice(index + 1)
            ]
        }
    };
};

const deleteEntry = (state, index) => {
    const key = getKeyName();
    return {
        ...state,
        [key]: {
            ...state[key],
            entries: [
                ...state[key].entries.slice(0, index),
                ...state[key].entries.slice(index + 1)
            ]
        }
    };
};

const deleteAll = state => {
    const key = getKeyName();
    return {
        ...state,
        [key]: {
            ...state[key],
            entries: []
        }
    };
};

const initialState = {
    additionalIncome: {},
    additionalIncome_CoApp: {}
};

const reducer = (state = initialState, action) => {
    const {type, payload} = action;
    switch (type) {
        case ADDITIONALINCOME_SETDATA: {
            const {additionalIncome, additionalIncome_CoApp} = payload;
            return {
                ...state,
                additionalIncome,
                additionalIncome_CoApp
            };
        }
        case ADDITIONALINCOME_SET: {
            const {key, value} = payload;
            return setState(state, key, value);
        }
        case ADDITIONALINCOME_ADD:
            return addMoreIncome(state);
        case ADDITIONALINCOME_DELETE:
            return deleteEntry(state, payload);
        case ADDITIONALINCOME_DELETE_ALL:
            return deleteAll(state, payload);
        case ADDITIONALINCOME_TYPE_CHANGE:
            return propertyChange(state, payload.index, payload.value, 'type');
        case ADDITIONALINCOME_AMOUNT_CHANGE:
            return propertyChange(state, payload.index, payload.value, 'amount');
        case ADDITIONALINCOME_DESCRIPTION_CHANGE:
            return propertyChange(state, payload.index, payload.value, 'description');
        case ADDITIONALINCOME_MONTH_CHANGE:
            return propertyChange(state, payload.index, payload.value, 'monthsWorked');
        case ADDITIONALINCOME_YEAR_CHANGE:
            return propertyChange(state, payload.index, payload.value, 'yearsWorked');
        case CLEAR_COAPP: {
            return {
                ...state,
                additionalIncome_CoApp: {}
            };
        }
    }

    return state;
};

class AdditionalIncomeService extends Service {
    constructor() {
        super();

        if (!AdditionalIncomeService.instance) {
            AdditionalIncomeService.instance = this;
        }
        return AdditionalIncomeService.instance;
    }

    getReducerName() {
        return reducerName;
    }

    getReducer() {
        return reducer;
    }

    getAdditionalIncomeForCurrentApplicant() {
        return this.getReducerState()[getKeyName()];
    }

    getFilteredEntries(targets, include = true) {
        const employmentInfo = EmploymentInfoService.getEmploymentInfoForCurrentApplicant();
        const {status} = employmentInfo;
        let {entries = []} = this.getAdditionalIncomeForCurrentApplicant();

        if (status === 'retired') {
            entries = entries.slice().filter(item => include ? targets.includes(item.type) : !targets.includes(item.type));
        }

        return entries;
    }

    hasAdditionalIncome(targets = []) {
        const {hasAdditionalIncome, entries = []} = this.getAdditionalIncomeForCurrentApplicant();
        const filteredEntries = entries.filter(entry => {
            const {amount, type} = entry;
            const isComplete = type !== '' && amount !== '';
            const noTargets = !targets.includes(type);
            return isComplete && noTargets;
        });
        // we don't store the `hasAdditionalIncome` flag, so use filteredEntries.length if we're loading from data
        return !!hasAdditionalIncome || !!filteredEntries.length;
    }

    getIncomeIndex(income) {
        const {entries = []} = this.getAdditionalIncomeForCurrentApplicant();
        const index = entries.findIndex(entry => entry.amount === income.amount && entry.type === income.type);

        return index !== -1 ? index : entries.length ? entries.length - 1 : 0;
    }

    add() {
        this.store.dispatch({
            type: ADDITIONALINCOME_ADD
        });
    }

    clearCoApplicant() {
        this.store.dispatch({
            type: CLEAR_COAPP
        });
    }

    delete(index) {
        this.store.dispatch({
            type: ADDITIONALINCOME_DELETE,
            payload: index
        });
    }

    deleteAll() {
        this.store.dispatch({
            type: ADDITIONALINCOME_DELETE_ALL
        });
    }

    typeChange(index, value) {
        this.store.dispatch({
            type: ADDITIONALINCOME_TYPE_CHANGE,
            payload: {index, value}
        });
    }

    amountChange(index, value) {
        this.store.dispatch({
            type: ADDITIONALINCOME_AMOUNT_CHANGE,
            payload: {index, value}
        });
    }

    descriptionChange(index, value) {
        this.store.dispatch({
            type: ADDITIONALINCOME_DESCRIPTION_CHANGE,
            payload: {index, value}
        });
    }

    monthChange(index, value) {
        this.store.dispatch({
            type: ADDITIONALINCOME_MONTH_CHANGE,
            payload: {index, value}
        });
    }

    yearChange(index, value) {
        this.store.dispatch({
            type: ADDITIONALINCOME_YEAR_CHANGE,
            payload: {index, value}
        });
    }

    set(key, value) {
        this.store.dispatch({
            type: ADDITIONALINCOME_SET,
            payload: {
                key,
                value
            }
        });
    }

    setData(data) {
        const {additionalIncome, additionalIncome_CoApp} = data;

        this.store.dispatch({
            type: ADDITIONALINCOME_SETDATA,
            payload: {
                additionalIncome,
                additionalIncome_CoApp
            }
        });
    }

    getDataForSave() {
        const additionalIncome = this.getAdditionalIncomeForCurrentApplicant();

        let entries = additionalIncome.entries || [];
        let newEntries = [];
        entries.forEach(entry => {
            if (entry.type && entry.amount) {
                newEntries.push(entry);
            }
        });

        return {
            [getKeyName()]: {
                entries: newEntries
            }
        };
    }
}

export default new AdditionalIncomeService();
